// Landing-page search mask modeled after the legacy DOPI application's start screen: a
// Produkte/Dokumente toggle, a free-text search term, and - behind the "Mehr Filteroptionen" button
// in the bottom-left of the card, since most searches don't need them - three MUTUALLY DEPENDENT
// multi-selects for the tree's group/family/system levels - like a dependent facet (see
// publixx-katalog's "Smart Graying"): picking any one level narrows/derives the other two, and the
// user may start at any level (not just Gruppe) - picking a Familie directly, for instance, adds
// its parent Gruppe and narrows System to that Familie's children. The most specific level with a
// selection is what counts: exactly ONE node there navigates the results page to that node as
// before (tree selection, breadcrumb, system view); several nodes become a SCOPE instead (see
// state.scopeNodeIdsAtom) - the results page cuts the catalog down to their subtrees and shows
// them above the breadcrumb. Multi-select was a customer ask for the landing page only; the
// results page deliberately has no permanent "System" facet.
//
// Below those (products mode only), the assortment entry the customer sketched (see chat): a
// "Weltweites Sortiment" checkbox - on by default, it stands for the DEFAULT_PRESET_KEY preset
// (Alle Länder + Lifecycle Wachstum) and locks the two selects it would
// contradict - and a row of Landesgesellschaft (multi), Lifecycle (multi) and Planbar ab (calendar).
// All of it is written into the very same filtersAtom the results page reads, so the choice is
// visible and changeable in the results page's filter drawer afterwards, and the live Treffer
// count here is computed with exactly these filters.

import {
    Box,
    Button,
    Card,
    Checkbox,
    Collapse,
    Group,
    MultiSelect,
    Paper,
    Progress,
    Radio,
    Select,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Title,
    UnstyledButton,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue, useDisclosure, useReducedMotion } from '@mantine/hooks';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { brand } from '../../branding';
import {
    applyFilters,
    buildIndexByArtNr,
    buildTreeIndex,
    byKey,
    categoryCounts,
    compareText,
    distinctDocumentLanguages,
    distinctDocumentTypes,
    humanizeDocType,
    inAnyCategory,
    inAnyDocumentCategory,
    languageLabel,
    lovLabel,
    orderFacetValues,
    searchTree,
    selectCatalog,
    selectDocuments,
    type MediaGroup,
} from '../engine';
import { useCatalogLabels } from '../labels';
import { DATA_LANGUAGE, useCatalogConfig, useCatalogIndex, useDocumentIndex, useLabels, useTree } from '../queries';
import { dateToIso, isoToDate } from './dateIso';
import { MOBILE_H1_STYLE, useIsMobile } from './mobileLayout';
import { selectRightSection, selectRightSectionPointerEvents } from './selectAffordances';
import { TreeSearchHits } from './TreeSearchHits';
import { useLoadProgress } from './useLoadProgress';
import { UserGreeting } from './UserGreeting';
import {
    columnFiltersAtom,
    documentDocTypesAtom,
    documentEditionsAtom,
    documentLanguageAtom,
    filtersAtom,
    scopeNodeIdsAtom,
    searchAtom,
    searchModeAtom,
    selectedDocumentNodeIdAtom,
    selectedNodeIdAtom,
    sortAtom,
} from '../state';
import {
    loc,
    type ActiveFilters,
    type CatalogConfig,
    type CatalogLabels,
    type DocumentIndexItem,
    type IndexItem,
    type Tree,
    type TreeNode,
    type TreeNodeType,
} from '../types';

// The preset (catalog-config.json) behind the "Weltweites Sortiment" checkbox: its filters are what
// the checkbox applies - no local-only/discontinued articles unless the user unticks it. Read from
// config rather than spelled out here, so the definition stays in one place.
const DEFAULT_PRESET_KEY = 'global-program';

// The three facets the assortment row below the checkbox is made of. Each control only renders if
// its facet is actually configured (see assortmentFacet), so a config without one of them simply
// loses that field rather than breaking the mask.
const SUBSIDIARY_FACET_KEY = 'subsidiary';
const LIFECYCLE_FACET_KEY = 'lifecycle_group';
const PLANNABLE_FACET_KEY = 'article_plannable_from';

// How many top hits the Google-like instant-answer dropdown shows underneath the search field -
// deliberately small, this is a jump-straight-to-it shortcut, not a second results list ("alle
// Ergebnisse anzeigen" is still what the submit button is for).
const SUGGESTION_LIMIT = 6;

// How long the data-loading progress stays a plain progress bar before it also spells out the
// Chrome/OIDC-storage-access hint (see the isDataReady block below) - a genuinely slow but normal
// load shouldn't flash the hint immediately.
const STUCK_HINT_DELAY_MS = 3000;

const flattenTree = (nodes: Tree): TreeNode[] => nodes.flatMap((node) => [node, ...flattenTree(node.children)]);

/** Every node's direct parent, keyed by nodeId - the tree itself only has children[] pointers. */
const buildParentMap = (nodes: Tree): Map<string, TreeNode> => {
    const parentMap = new Map<string, TreeNode>();
    const walk = (list: TreeNode[], parent?: TreeNode) => {
        list.forEach((node) => {
            if (parent) {
                parentMap.set(node.nodeId, parent);
            }
            walk(node.children, node);
        });
    };
    walk(nodes);
    return parentMap;
};

/** Walks up from `nodeId` (inclusive) to the nearest ancestor of `type`, or undefined if none. */
const ancestorOfType = (
    nodeById: Map<string, TreeNode>,
    parentMap: Map<string, TreeNode>,
    nodeId: string,
    type: TreeNodeType
): TreeNode | undefined => {
    let current = nodeById.get(nodeId);
    while (current) {
        if (current.type === type) {
            return current;
        }
        current = parentMap.get(current.nodeId);
    }
    return undefined;
};

// Real STIBO deliveries can leave a node's name attribute empty (mostly seen on `system` leaves) -
// rather than surface the raw nodeId (a confusing UUID, see chat), such nodes are simply left out
// of the select options entirely; they stay in allNodes/parentMap so ancestor lookups still work.
const toSortedOptions = (nodes: TreeNode[], locale: string) =>
    nodes
        .map((node) => ({ value: node.nodeId, label: loc(node.name, locale, '') }))
        .filter((option) => option.label !== '')
        .sort((a, b) => compareText(a.label, b.label, locale));

/** Select options for a value-list facet, from the FULL index (options never shrink because of
 * other selections, same convention as the document filters) with the LOV clear text from
 * labels.<lang>.json (FacetDef.attrId) - "Alle Länder" rather than "AllCountries". */
const lovOptions = (
    config: CatalogConfig | undefined,
    items: IndexItem[],
    labels: CatalogLabels | undefined,
    facetKey: string,
    locale: string
) => {
    const facet = config?.facets.find((def) => def.key === facetKey);
    if (!facet || !labels) {
        return [];
    }
    const values = new Set<string>();
    items.forEach((item) => {
        const raw = item[facetKey];
        (Array.isArray(raw) ? raw : [raw]).forEach((value) => {
            if (typeof value === 'string' && value) {
                values.add(value);
            }
        });
    });
    // Alphabetical by clear text with the facet's own pinning ("Alle Länder" first) - a Select
    // wants that order regardless of the facet's `sort`, which only decides the drawer's default.
    return orderFacetValues(
        [...values].map((value) => ({ value, label: lovLabel(labels, facet, value) })),
        { ...facet, sort: 'label' },
        labels,
        locale
    );
};

export const SearchMask = () => {
    const { locale, L } = useCatalogLabels();
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const treeQuery = useTree();
    const indexQuery = useCatalogIndex(DATA_LANGUAGE);
    const configQuery = useCatalogConfig();
    // LOV clear text for the assortment selects (Landesgesellschaft/Lifecycle) - see lovOptions.
    const labelsQuery = useLabels(locale);
    const [, setSearch] = useAtom(searchAtom);
    const [, setSearchMode] = useAtom(searchModeAtom);
    const [, setSelectedNodeId] = useAtom(selectedNodeIdAtom);
    const [, setSelectedDocumentNodeId] = useAtom(selectedDocumentNodeIdAtom);
    const [, setScopeNodeIds] = useAtom(scopeNodeIdsAtom);
    const [, setFilters] = useAtom(filtersAtom);
    const [, setColumnFilters] = useAtom(columnFiltersAtom);
    const [, setSort] = useAtom(sortAtom);
    const [, setDocumentLanguageAtom] = useAtom(documentLanguageAtom);
    const [, setDocumentDocTypesAtom] = useAtom(documentDocTypesAtom);
    const [, setDocumentEditionsAtom] = useAtom(documentEditionsAtom);

    // Produkte is the default entry point (see chat: "Die Radiobox 'Produkte' soll vor 'Dokumente'
    // stehen und vorausgewählt sein") - product data is what most users come here for; documents are
    // the deliberate second choice.
    const [mode, setMode] = useState<'products' | 'documents'>('products');

    // Bound to the local `mode` rather than pinned to `true`, and deliberately placed after it.
    // documents.json is by far the largest catalog artifact - measured 1.9 MB over 1,307 articles,
    // which extrapolates to ~120 MB raw / 17.4 MB gzipped at 100,000 - and it sits in `dataQueries`
    // below, which gates the whole mask. Force-enabling it meant the LANDING PAGE stayed unusable
    // until the entire document index had downloaded and parsed, for a mode most users never open:
    // roughly 72% of the startup budget. See docs/09-ladezeit-messungen.md for the measurements.
    //
    // The cost is the trade the `dataQueries` comment below used to buy: switching to "Dokumente"
    // now shows a brief loading state instead of an instantly populated filter. That is the right
    // way round - the wait moves from everyone to the few who ask for it.
    //
    // Every consumer is already safe: scopedDocuments, documentLanguages and availableDocTypes all
    // fall back to `[]`, and previewCount's document branch returns undefined while data is absent.
    const documentIndexQuery = useDocumentIndex(mode === 'documents');
    const [term, setTerm] = useState('');
    const [groupIds, setGroupIds] = useState<string[]>([]);
    const [familyIds, setFamilyIds] = useState<string[]>([]);
    const [systemIds, setSystemIds] = useState<string[]>([]);
    // Document-mode advanced filters (Sprache/Dokumentenart) - local until runSearch pushes them to
    // the shared atoms, same as groupId/familyId/systemId above and for the same reason (this is a
    // draft the user is still composing; CatalogPage only needs to know about it on submit).
    const [documentLanguage, setDocumentLanguage] = useState<string | null>(null);
    const [documentDocTypes, setDocumentDocTypes] = useState<string[]>([]);
    // Product-mode assortment filters (see file comment): "Weltweites Sortiment" is the default
    // entry; while it is on, the Landesgesellschaft/Lifecycle selects are locked (their values are
    // cleared on ticking it, so nothing stale lingers behind a disabled field). "Planbar ab" is
    // independent of the checkbox and stays usable either way. Local draft state like the rest;
    // applySearchState turns it into filtersAtom on submit.
    const [isGlobalAssortment, setIsGlobalAssortment] = useState(true);
    const [subsidiaries, setSubsidiaries] = useState<string[]>([]);
    const [lifecycles, setLifecycles] = useState<string[]>([]);
    // ISO "YYYY-MM-DD" (the engine's own date format), Date only ever exists inside the picker.
    const [plannableUntil, setPlannableUntil] = useState<string | undefined>(undefined);
    // Drives the instant-answer dropdown: shown while the field has focus, hidden on blur/Escape/
    // navigating away. highlightedIndex is the arrow-key cursor into the current suggestion list
    // (-1 = none highlighted, Enter falls through to the normal runSearch).
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    // "Mehr Filteroptionen" - collapsed by default, toggled by the button in the bottom-left of the
    // card (see the button row at the end of this file). Was a Mantine Accordion; the customer asked
    // for a plain button instead ("Mehr filteroptionen über einen Button unten links").
    const [areFiltersOpen, { toggle: toggleFilters }] = useDisclosure(false);

    const tree = useMemo(() => treeQuery.data ?? [], [treeQuery.data]);
    const allNodes = useMemo(() => flattenTree(tree), [tree]);
    const nodeById = useMemo(() => new Map(allNodes.map((node) => [node.nodeId, node])), [allNodes]);
    const parentMap = useMemo(() => buildParentMap(tree), [tree]);

    // Gruppe is always the full, unfiltered list - it's the top level, nothing narrows it further.
    const groupOptions = useMemo(
        () => toSortedOptions(allNodes.filter((node) => node.type === 'group'), locale),
        [allNodes, locale]
    );
    /** The nearest ancestor of `type` above `id`, as an id (undefined at the top). */
    const ancestorId = useCallback(
        (id: string, type: TreeNodeType) => ancestorOfType(nodeById, parentMap, id, type)?.nodeId,
        [nodeById, parentMap]
    );
    const hasAncestorIn = useCallback(
        (id: string, type: TreeNodeType, ids: string[]) => {
            const ancestor = ancestorId(id, type);
            return ancestor !== undefined && ids.includes(ancestor);
        },
        [ancestorId]
    );
    // Familie narrows to the selected Gruppen's children, if any are selected.
    const familyOptions = useMemo(
        () =>
            toSortedOptions(
                allNodes.filter(
                    (node) => node.type === 'family' && (groupIds.length === 0 || hasAncestorIn(node.nodeId, 'group', groupIds))
                ),
                locale
            ),
        [allNodes, hasAncestorIn, groupIds, locale]
    );
    // System narrows to the selected Familien's children; falling back to the selected Gruppen's
    // descendants if only Gruppen (no Familie yet) are selected.
    const systemOptions = useMemo(
        () =>
            toSortedOptions(
                allNodes.filter((node) => {
                    if (node.type !== 'system') {
                        return false;
                    }
                    if (familyIds.length > 0) {
                        return hasAncestorIn(node.nodeId, 'family', familyIds);
                    }
                    if (groupIds.length > 0) {
                        return hasAncestorIn(node.nodeId, 'group', groupIds);
                    }
                    return true;
                }),
                locale
            ),
        [allNodes, hasAncestorIn, familyIds, groupIds, locale]
    );

    // The most specific level with a selection wins (as it did with single selects). One node is
    // the classic single-node navigation (`nodeId`); several are the multi-select SCOPE
    // (`scopeNodeIds`, see file comment). Both feed the live preview below and runSearch further
    // down.
    let chosenNodeIds = groupIds;
    if (systemIds.length > 0) {
        chosenNodeIds = systemIds;
    } else if (familyIds.length > 0) {
        chosenNodeIds = familyIds;
    }
    // With a search term, even ONE chosen node travels as the scope: on the results page a search
    // always covers the whole catalog and ignores the tree selection (the Suchbereich switch is
    // gone), so a single node passed as the selection would be silently dropped from the search.
    // As a scope it narrows the hits and shows as a removable "Eingegrenzt auf" chip.
    const hasTerm = term.trim().length >= (configQuery.data?.search.minChars ?? 2);
    const nodeId = chosenNodeIds.length === 1 && !hasTerm ? chosenNodeIds[0] : undefined;
    const scopeNodeIds = useMemo(
        () => (chosenNodeIds.length > 1 || (chosenNodeIds.length === 1 && hasTerm) ? chosenNodeIds : []),
        [chosenNodeIds, hasTerm]
    );
    const treeIndex = useMemo(() => buildTreeIndex(tree), [tree]);
    const fullIndexByArtNr = useMemo(() => buildIndexByArtNr(indexQuery.data ?? []), [indexQuery.data]);
    // Scoped the same way CatalogPage does it (once, up front, both indexes), so the preview count
    // is the number the results page will show.
    const scopedIndex = useMemo(() => {
        const items = indexQuery.data ?? [];
        return scopeNodeIds.length > 0 ? items.filter((item) => inAnyCategory(item, scopeNodeIds, treeIndex)) : items;
    }, [indexQuery.data, scopeNodeIds, treeIndex]);
    const scopedDocuments = useMemo(() => {
        const docs = documentIndexQuery.data ?? [];
        return scopeNodeIds.length > 0
            ? docs.filter((doc) => inAnyDocumentCategory(doc, scopeNodeIds, treeIndex, fullIndexByArtNr))
            : docs;
    }, [documentIndexQuery.data, scopeNodeIds, treeIndex, fullIndexByArtNr]);
    const indexByArtNr = useMemo(() => buildIndexByArtNr(scopedIndex), [scopedIndex]);
    // Document-mode advanced-filter options - built from the FULL document set, same convention as
    // CatalogPage's own documentLanguages/availableDocTypes (DocumentFilterBar/DocumentFacetsPanel):
    // options never shrink just because the user already picked one.
    const documentLanguages = useMemo(
        () => distinctDocumentLanguages(documentIndexQuery.data ?? [], locale),
        [documentIndexQuery.data, locale]
    );
    const availableDocTypes = useMemo(
        () => distinctDocumentTypes(documentIndexQuery.data ?? []),
        [documentIndexQuery.data]
    );
    const [debouncedTerm] = useDebouncedValue(term, 250);

    const assortmentFacet = (key: string) => configQuery.data?.facets.find((def) => def.key === key);
    const subsidiaryFacet = assortmentFacet(SUBSIDIARY_FACET_KEY);
    const lifecycleFacet = assortmentFacet(LIFECYCLE_FACET_KEY);
    const plannableFacet = assortmentFacet(PLANNABLE_FACET_KEY);
    const subsidiaryOptions = useMemo(
        () => lovOptions(configQuery.data, indexQuery.data ?? [], labelsQuery.data, SUBSIDIARY_FACET_KEY, locale),
        [configQuery.data, indexQuery.data, labelsQuery.data, locale]
    );
    const lifecycleOptions = useMemo(
        () => lovOptions(configQuery.data, indexQuery.data ?? [], labelsQuery.data, LIFECYCLE_FACET_KEY, locale),
        [configQuery.data, indexQuery.data, labelsQuery.data, locale]
    );
    const presetFilters = useMemo(
        () => configQuery.data?.presets?.find((preset) => preset.key === DEFAULT_PRESET_KEY)?.filters ?? {},
        [configQuery.data]
    );
    // What the checkbox stands for, spelled out under it in clear text ("Alle Länder · Wachstum")
    // - built from the preset itself so config and caption can't drift apart. Deliberately NOT
    // objectTypeId=Article_central any more: "Alle Länder" articles are central by construction
    // (the converter derives subsidiary from the winning record), so the extra filter changed
    // nothing for the preset itself but locked every Landesgesellschaft to 0 in the drawer - all
    // country articles are Article_local, and unticking "Alle Länder" alone could never reach them.
    const presetCaption = useMemo(
        () =>
            Object.entries(presetFilters)
                .filter((entry): entry is [string, string[]] => Array.isArray(entry[1]))
                .flatMap(([key, values]) => {
                    const facet = configQuery.data?.facets.find((def) => def.key === key) ?? { key };
                    const labels = labelsQuery.data;
                    return values.map((value) => (labels ? lovLabel(labels, facet, value) : value));
                })
                .join(' · '),
        [presetFilters, configQuery.data, labelsQuery.data]
    );
    // The product filters this mask contributes - the ONE definition both the live preview count
    // and the submit (applySearchState) use, so the Treffer number promised here is exactly what
    // the results page shows. "Planbar ab" <= date: articles plannable by that date at the latest
    // (engine.applyFilters compares the ISO strings, `to` is inclusive).
    const maskFilters = useMemo((): ActiveFilters => {
        const filters: ActiveFilters = isGlobalAssortment ? { ...presetFilters } : {};
        if (!isGlobalAssortment && subsidiaries.length > 0) {
            filters[SUBSIDIARY_FACET_KEY] = subsidiaries;
        }
        if (!isGlobalAssortment && lifecycles.length > 0) {
            filters[LIFECYCLE_FACET_KEY] = lifecycles;
        }
        if (plannableUntil) {
            filters[PLANNABLE_FACET_KEY] = { to: plannableUntil };
        }
        return filters;
    }, [isGlobalAssortment, presetFilters, subsidiaries, lifecycles, plannableUntil]);

    // Live result preview - runs the SAME engine functions the real catalog page uses
    // (selectCatalog/selectDocuments), just with perPage capped at SUGGESTION_LIMIT instead of a
    // real page size, so a user sees both how many hits a search/filter combination yields AND its
    // top few hits before navigating anywhere (see chat: "quasi die Suche triggern, ohne was
    // anzuzeigen", and later the Google-like instant-answer dropdown). Products mode also applies
    // the assortment filters (maskFilters: "Weltweites Sortiment" preset, or Landesgesellschaft/
    // Lifecycle/Planbar ab) so the preview matches what actually shows up after clicking through -
    // a free-text search is narrowed by those filters too, exactly as it will be after navigating
    // (see engine.filterSearchHits).
    //
    // A discriminated union rather than one merged item shape: products (IndexItem) and documents
    // (MediaGroup<DocumentIndexItem>) render as genuinely different suggestion rows below, and this
    // keeps that branch type-checked instead of casting.
    const previewResult = useMemo(():
        | { mode: 'products'; total: number; items: IndexItem[] }
        | { mode: 'documents'; total: number; items: MediaGroup<DocumentIndexItem>[] }
        | undefined => {
        const config = configQuery.data;
        if (!config) {
            return undefined;
        }
        if (mode === 'products') {
            if (!indexQuery.data) {
                return undefined;
            }
            const result = selectCatalog(scopedIndex, config, {
                selectedNodeId: nodeId,
                search: debouncedTerm,
                filters: maskFilters,
                sort: { key: 'name', dir: 'asc' },
                page: 1,
                perPage: SUGGESTION_LIMIT,
                treeIndex,
            });
            return { mode: 'products', total: result.total, items: result.items };
        }
        if (!documentIndexQuery.data) {
            return undefined;
        }
        const result = selectDocuments(scopedDocuments, indexByArtNr, treeIndex, config.search.minChars, {
            search: debouncedTerm,
            language: documentLanguage ?? undefined,
            docTypes: documentDocTypes,
            nodeId,
            page: 1,
            perPage: SUGGESTION_LIMIT,
        });
        return { mode: 'documents', total: result.total, items: result.items };
    }, [
        configQuery.data,
        mode,
        debouncedTerm,
        nodeId,
        indexQuery.data,
        documentIndexQuery.data,
        scopedIndex,
        scopedDocuments,
        indexByArtNr,
        treeIndex,
        documentLanguage,
        documentDocTypes,
        maskFilters,
    ]);
    const previewCount = previewResult?.total;

    // The dropdown only makes sense once the user is actually SEARCHING (matches selectCatalog's
    // own "searching" gate) - below minChars, `previewResult.items` is just the alphabetically-first
    // slice of the whole browse list, not a meaningful suggestion set.
    const minChars = configQuery.data?.search.minChars ?? Infinity;
    const isSearching = debouncedTerm.trim().length >= minChars;
    const suggestions = isSearchFocused && isSearching ? (previewResult?.items ?? []) : [];
    // The tree's own hits (system/family/group names), the same engine function and component the
    // results page uses below its article list - here in compact form at the bottom of the
    // dropdown. Products mode only; within the multi-select scope, if one is chosen.
    // Nodes with nothing under the assortment filters are dropped, as on the results page.
    const maskCounts = useMemo(() => {
        const config = configQuery.data;
        return config ? categoryCounts(applyFilters(scopedIndex, maskFilters, byKey(config.facets)), treeIndex) : {};
    }, [configQuery.data, scopedIndex, maskFilters, treeIndex]);
    const treeHits = useMemo(
        () =>
            mode === 'products' && isSearching
                ? searchTree(treeIndex, debouncedTerm, locale, scopeNodeIds, maskCounts)
                : undefined,
        [mode, isSearching, treeIndex, debouncedTerm, locale, scopeNodeIds, maskCounts]
    );
    const showTreeHits =
        isSearchFocused && treeHits !== undefined && treeHits.system.length + treeHits.family.length + treeHits.group.length > 0;
    const showSuggestions = suggestions.length > 0 || showTreeHits;
    // Split back out by mode for rendering below - narrowing `previewResult.items` through a local
    // const (rather than inline `previewResult?.mode === 'products' && previewResult.items...` in
    // the JSX) keeps the IndexItem/MediaGroup union type-checked without relying on TS's narrowing
    // reaching across an optional-chained `&&`.
    const productSuggestions = showSuggestions && previewResult?.mode === 'products' ? previewResult.items : undefined;
    const documentSuggestions = showSuggestions && previewResult?.mode === 'documents' ? previewResult.items : undefined;

    const selectSuggestion = (path: string) => {
        setIsSearchFocused(false);
        // Push the search into the shared catalog state before jumping to the hit: an article opens
        // as an overlay over the catalog list (see components/DetailOverlay.tsx), and this makes
        // that list the one the user actually searched for instead of the default browse list.
        applySearchState();
        navigate(path);
    };

    // Four queries fire on first load; documentIndexQuery only once `mode` is 'documents' (see its
    // comment above). The mask stays unusable until every ENABLED one has settled - `previewCount`
    // would otherwise flash a wrong number (e.g. the whole catalog's total, ignoring hierarchy)
    // while `treeIndex` is still built from an empty tree.
    //
    // A disabled query reports isLoading === false in TanStack Query v5 (isPending && isFetching),
    // so leaving documentIndexQuery in this list costs nothing while it is off, and re-arms the
    // gate the moment the user switches to 'documents' - which is exactly the intent.
    const dataQueries = [treeQuery, indexQuery, configQuery, documentIndexQuery, labelsQuery];
    const loadedQueryCount = dataQueries.filter((query) => !query.isLoading).length;
    const isDataReady = loadedQueryCount === dataQueries.length;
    // The bar keeps creeping between the real marks, and the text names what is being waited for -
    // "4/5" alone gave no hint that the LAST step (the article index) is the long one, and a bar that
    // stands still for several seconds reads as a hang (see useLoadProgress).
    const loadProgress = useLoadProgress(loadedQueryCount, dataQueries.length, isDataReady);
    const pendingStep = [
        { query: treeQuery, label: L.loadingStepTree },
        { query: indexQuery, label: L.loadingStepIndex },
        { query: configQuery, label: L.loadingStepConfig },
        { query: documentIndexQuery, label: L.loadingStepDocuments },
        { query: labelsQuery, label: L.loadingStepLabels },
    ].find((step) => step.query.isLoading)?.label;
    const reducedMotion = useReducedMotion();

    // Chrome (not Firefox) can silently stall EVERY authenticated request behind an OIDC
    // silent-renew iframe that needs a real user gesture before it's allowed to touch storage
    // (Storage Access API / third-party-cookie phase-out) - see chat. A SYNTHETIC click can't
    // satisfy that requirement (browsers deliberately reject scripted gestures for exactly this
    // API, precisely to stop this kind of workaround), so there is no way to trigger it from code.
    // What DOES work: the very next genuine click/keypress the user makes ANYWHERE unblocks the
    // already-pending request - it doesn't have to land on this form. Below a few seconds this
    // just looks like normal loading; past that, spell it out rather than leave the progress bar
    // stuck at 0/5 with no explanation.
    const [showStuckHint, setShowStuckHint] = useState(false);
    useEffect(() => {
        if (isDataReady) {
            setShowStuckHint(false);
            return;
        }
        const timer = setTimeout(() => setShowStuckHint(true), STUCK_HINT_DELAY_MS);
        return () => clearTimeout(timer);
    }, [isDataReady]);

    const union = (current: string[], added: (string | undefined)[]) => [
        ...new Set([...current, ...added.filter((id): id is string => id !== undefined)]),
    ];
    // Changing the Gruppen drops every Familie/System no longer under one of them - and all of
    // them once no Gruppe is left (the single-select rule "changing Gruppe clears below", set-wise).
    const handleGroupsChange = (values: string[]) => {
        setGroupIds(values);
        setFamilyIds((prev) => prev.filter((id) => hasAncestorIn(id, 'group', values)));
        setSystemIds((prev) => prev.filter((id) => hasAncestorIn(id, 'group', values)));
    };
    // Picking Familien adds their parent Gruppen automatically (upward is unambiguous) and drops
    // Systeme that no longer belong to one of the chosen Familien.
    const handleFamiliesChange = (values: string[]) => {
        setFamilyIds(values);
        setGroupIds((prev) => union(prev, values.map((id) => ancestorId(id, 'group'))));
        setSystemIds((prev) => prev.filter((id) => hasAncestorIn(id, 'family', values)));
    };
    // Picking Systeme adds both their parent Familien and Gruppen automatically.
    const handleSystemsChange = (values: string[]) => {
        setSystemIds(values);
        setFamilyIds((prev) => union(prev, values.map((id) => ancestorId(id, 'family'))));
        setGroupIds((prev) => union(prev, values.map((id) => ancestorId(id, 'group'))));
    };

    const handleGlobalAssortmentChange = (checked: boolean) => {
        setIsGlobalAssortment(checked);
        if (checked) {
            setSubsidiaries([]);
            setLifecycles([]);
        }
    };

    /** Everything the search mask contributes to the shared catalog state - without navigating, so
     * both the submit button and a picked suggestion can use it (see selectSuggestion above).
     * `overrides` is for a picked TREE hit (selectTreeNode): that node instead of the selects'
     * choice, no search term, no scope - "go there". */
    const applySearchState = (overrides?: { search: string; nodeId: string | undefined; scopeNodeIds: string[] }) => {
        const effectiveNodeId = overrides ? overrides.nodeId : nodeId;
        setSearch(overrides ? overrides.search : term);
        setSearchMode(mode);
        // A search-mask navigation is meant to start fresh on /catalog - without this, leftover
        // facet/column filters and sort from a PREVIOUS catalog visit would silently narrow/reorder
        // this new search. The product filters are exactly this mask's own (maskFilters) -
        // nothing more, nothing carried over; on the results page they stay visible as removable
        // chips under the toolbar.
        setFilters(mode === 'products' ? maskFilters : {});
        // Several chosen nodes travel as the scope (both modes), one as the plain selection below.
        setScopeNodeIds(overrides ? overrides.scopeNodeIds : scopeNodeIds);
        setColumnFilters({});
        setSort({ key: 'name', dir: 'asc' });
        if (mode === 'products') {
            setSelectedNodeId(effectiveNodeId);
            setSelectedDocumentNodeId(undefined);
            setDocumentLanguageAtom(null);
            setDocumentDocTypesAtom([]);
        } else {
            setSelectedDocumentNodeId(effectiveNodeId);
            setSelectedNodeId(undefined);
            setDocumentLanguageAtom(documentLanguage);
            setDocumentDocTypesAtom(documentDocTypes);
        }
        // No edition UI on this landing mask - always reset, so the Treffer count promised here
        // never silently differs from the results page because of a stale drawer-set edition
        // filter (this mask's own preview count runs without an edition restriction).
        setDocumentEditionsAtom([]);
    };

    /** The "Alle N Treffer anzeigen" row of the dropdown - the plain submit, dropdown closed. */
    const showAllHits = () => {
        setIsSearchFocused(false);
        runSearch();
    };

    /** A picked tree hit: straight into that node's list on the results page, search cleared. */
    const selectTreeNode = (treeNodeId: string) => {
        setIsSearchFocused(false);
        applySearchState({ search: '', nodeId: treeNodeId, scopeNodeIds: [] });
        navigate('/catalog');
    };

    // Plain /catalog, no `?preset=` any more: the assortment choice already IS in filtersAtom (see
    // applySearchState), and routing through the preset parameter on top would re-apply the
    // "Weltweites Sortiment" filters even when the user just unticked that box.
    const runSearch = () => {
        applySearchState();
        navigate('/catalog');
    };

    // `item`'s real shape depends on `mode` (IndexItem for products, MediaGroup<DocumentIndexItem>
    // for documents) - safe to assume here because `suggestions` is always previewResult.items for
    // the SAME `mode` value this closure captures (previewResult recomputes whenever mode changes).
    const pathForSuggestion = (item: IndexItem | MediaGroup<DocumentIndexItem>): string =>
        mode === 'products'
            ? `/catalog/article/${encodeURIComponent((item as IndexItem).artNr)}`
            : `/catalog/document/${encodeURIComponent((item as MediaGroup<DocumentIndexItem>).primary.assetId)}`;

    return (
        // Google-like: a centered, width-capped card rather than a full-bleed bar - maw+mx="auto"
        // centers it on wide viewports while still shrinking to fill narrow/mobile ones. Padding is
        // an explicit px value (Mantine's spacing scale tops out at "xl" ~ 32px, not generous enough
        // for a hero-style search box) rather than one of the named size keys.
        <Card maw={760} mx="auto" p={40} radius="md" shadow="xs" withBorder>
            <Stack gap="lg">
                {/* Left-aligned, greeting and headline both (see chat + screenshot: "Wonach suchen
                    sie soll linksbündig sein. Auch die Headline.") - the centred hero version read
                    as a marketing splash; the mask below it is a form, and a form starts at the
                    left edge. */}
                <Stack align="flex-start" gap={4}>
                    <UserGreeting />
                    {/* order={1}: this is the landing page's top heading, and the heading scale
                        (getCustomThemeOverrides) tints exactly that level in the brand blue.
                        It was order={2} back when App.css painted EVERY level blue anyway - at
                        which point the level carried no meaning. */}
                    <Title order={1} style={isMobile ? MOBILE_H1_STYLE : undefined}>
                        {L.searchMaskHeading}
                    </Title>
                </Stack>
                <Group align="center" gap="xl" wrap="wrap">
                    <Radio.Group
                        value={mode}
                        onChange={(value) => {
                            setMode(value as 'products' | 'documents');
                            setHighlightedIndex(-1);
                        }}
                    >
                        <Group gap="lg">
                            <Radio label={L.searchMaskProductData} size="md" value="products" />
                            <Radio label={L.searchModeDocuments} size="md" value="documents" />
                        </Group>
                    </Radio.Group>
                    <Box style={{ flex: 1, minWidth: 220, position: 'relative' }}>
                        <TextInput
                            label={L.searchMaskTerm}
                            placeholder={L.searchMaskPlaceholder}
                            size="md"
                            value={term}
                            onBlur={() => setIsSearchFocused(false)}
                            onChange={(event) => {
                                setTerm(event.currentTarget.value);
                                setHighlightedIndex(-1);
                            }}
                            onFocus={() => setIsSearchFocused(true)}
                            onKeyDown={(event) => {
                                if (event.key === 'ArrowDown' && suggestions.length > 0) {
                                    event.preventDefault();
                                    setHighlightedIndex((i) => (i + 1) % suggestions.length);
                                    return;
                                }
                                if (event.key === 'ArrowUp' && suggestions.length > 0) {
                                    event.preventDefault();
                                    setHighlightedIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
                                    return;
                                }
                                if (event.key === 'Enter') {
                                    const highlighted = suggestions[highlightedIndex];
                                    if (highlighted) {
                                        event.preventDefault();
                                        selectSuggestion(pathForSuggestion(highlighted));
                                        return;
                                    }
                                    runSearch();
                                    return;
                                }
                                if (event.key === 'Escape') {
                                    setIsSearchFocused(false);
                                }
                            }}
                        />
                        {/* Google-like instant answers: the field's own top hits, jump straight to
                            one instead of always routing through the full results list. Positioned
                            against this Box (not the whole Group) so it hugs just the input, not the
                            mode toggle beside it. onMouseDown/preventDefault on each row keeps focus
                            on the TextInput through the click, so this closes via the row's own
                            selectSuggestion(), not a blur race with the click. */}
                        {showSuggestions && (
                            <Paper mt={4} shadow="md" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10 }} withBorder>
                                <Stack gap={0} py={4}>
                                    {/* Section heading with the TOTAL, so the capped list below
                                        (SUGGESTION_LIMIT best-ranked hits) reads as a preview, not
                                        as "these are all". */}
                                    {suggestions.length > 0 && previewResult && (
                                        <Text c="dimmed" fw={500} pb={2} pt={4} px="sm" size="xs">
                                            {mode === 'products' ? L.searchMaskArticleHits : L.searchModeDocuments} (
                                            {previewResult.total})
                                        </Text>
                                    )}
                                    {productSuggestions?.map((item, index) => (
                                        <UnstyledButton
                                            key={item.artNr}
                                            px="sm"
                                            py={6}
                                            style={{ backgroundColor: index === highlightedIndex ? 'var(--pih-surface)' : undefined }}
                                            onClick={() => selectSuggestion(pathForSuggestion(item))}
                                            onMouseDown={(event) => event.preventDefault()}
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                        >
                                            <Group gap="sm" justify="space-between" wrap="nowrap">
                                                <Text fw={500} size="sm" truncate>
                                                    {item.name}
                                                </Text>
                                                <Text c="dimmed" size="xs" style={{ flexShrink: 0 }}>
                                                    {item.artNr}
                                                </Text>
                                            </Group>
                                        </UnstyledButton>
                                    ))}
                                    {documentSuggestions?.map((group, index) => (
                                        <UnstyledButton
                                            key={group.primary.documentId ?? group.primary.assetId}
                                            px="sm"
                                            py={6}
                                            style={{ backgroundColor: index === highlightedIndex ? 'var(--pih-surface)' : undefined }}
                                            onClick={() => selectSuggestion(pathForSuggestion(group))}
                                            onMouseDown={(event) => event.preventDefault()}
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                        >
                                            <Stack gap={0}>
                                                <Text fw={500} size="sm" truncate>
                                                    {group.primary.descr ?? group.primary.assetId}
                                                </Text>
                                                <Text c="dimmed" size="xs">
                                                    {[
                                                        group.primary.docType ? humanizeDocType(group.primary.docType) : undefined,
                                                        group.primary.editionDate ?? undefined,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                </Text>
                                            </Stack>
                                        </UnstyledButton>
                                    ))}
                                    {/* More hits than the preview shows: one row to the full
                                        results list - the same thing the submit button does. */}
                                    {suggestions.length > 0 && previewResult && previewResult.total > suggestions.length && (
                                        <UnstyledButton
                                            px="sm"
                                            py={6}
                                            onClick={showAllHits}
                                            onMouseDown={(event) => event.preventDefault()}
                                        >
                                            <Text c={brand.primary} fw={500} size="sm">
                                                {L.searchMaskShowAllHits.replace('{count}', String(previewResult.total))}
                                            </Text>
                                        </UnstyledButton>
                                    )}
                                    {/* Same mouseDown guard as the rows above: keep the input's
                                        focus through the click, so the dropdown closes via
                                        selectTreeNode, not a blur race. */}
                                    {showTreeHits && treeHits && (
                                        <Box
                                            px="sm"
                                            py={6}
                                            style={
                                                suggestions.length > 0
                                                    ? { borderTop: '1px solid var(--mantine-color-default-border)' }
                                                    : undefined
                                            }
                                            onMouseDown={(event) => event.preventDefault()}
                                        >
                                            <TreeSearchHits hits={treeHits} compact onSelect={selectTreeNode} />
                                        </Box>
                                    )}
                                </Stack>
                            </Paper>
                        )}
                    </Box>
                </Group>
                {/* Control row: "Mehr Filteroptionen" on the LEFT (see chat + screenshot), Treffer
                    count and submit on the right. The filter panel it toggles hangs BELOW it. */}
                <Group align="center" justify="space-between" wrap="wrap">
                    {/* Explicit `styles` (which Mantine applies as INLINE styles, so they beat the
                        @uag/doka-design stylesheet) rather than a Mantine variant: `variant="subtle"`
                        came out as a solid dark-blue block under this app's theme, i.e. visually a
                        second primary button competing with "Ergebnisse anzeigen" - and with px={0}
                        its label sat flush against that block's edges (see chat: "Das Padding ist zu
                        eng"). Outlined, normal padding, clearly secondary. */}
                    <Button
                        aria-expanded={areFiltersOpen}
                        leftSection={
                            <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden>
                                {areFiltersOpen ? 'expand_less' : 'expand_more'}
                            </span>
                        }
                        px="lg"
                        size="md"
                        styles={{ root: { backgroundColor: 'transparent', border: `1px solid ${brand.primary}`, color: brand.primary } }}
                        variant="default"
                        onClick={toggleFilters}
                    >
                        {L.searchMaskMoreFilters}
                    </Button>
                    <Group align="center" gap="md" wrap="wrap">
                        {isDataReady ? (
                            // Same hint as the results page's toolbar while the term is too short
                            // to search with - the count would describe the unsearched catalog.
                            term.trim().length > 0 && term.trim().length < minChars ? (
                                <Text c="dimmed" size="sm">
                                    {L.searchMinChars.replace('{count}', String(minChars))}
                                </Text>
                            ) : (
                                previewCount !== undefined && (
                                    <Text c="dimmed" size="sm">
                                        {previewCount} {L.results}
                                    </Text>
                                )
                            )
                        ) : (
                            <Stack align="flex-end" gap={2}>
                                <Group gap="xs" wrap="nowrap">
                                    <Progress
                                        animated={!reducedMotion}
                                        aria-label={L.searchMaskLoading}
                                        size="sm"
                                        style={{ width: 120 }}
                                        value={loadProgress}
                                        striped
                                    />
                                    <Text c="dimmed" size="sm">
                                        {L.searchMaskLoading} ({loadedQueryCount}/{dataQueries.length})
                                        {pendingStep ? `: ${pendingStep}` : ''}
                                    </Text>
                                </Group>
                                {/* Chrome/OIDC-storage-access hint (see isDataReady/showStuckHint above) -
                                    only surfaces once loading has genuinely taken a while. */}
                                {showStuckHint && (
                                    <Text c="dimmed" size="xs" ta="right">
                                        {L.searchMaskStuckHint}
                                    </Text>
                                )}
                            </Stack>
                        )}
                        <Button disabled={!isDataReady} size="md" onClick={runSearch}>
                            {L.searchMaskSubmit}
                        </Button>
                    </Group>
                </Group>
                {/* The filters the "Mehr Filteroptionen" button above toggles - BELOW that button
                    (see chat: "Beim Klick auf 'Mehr Filteroptionen' sollten die Filter UNTER dem
                    Knopf erscheinen"), not above it, and a plain Collapse rather than the Accordion
                    this used to be, since the control now sits in the button row instead of on top of
                    its own panel. Collapsed by default: most searches never need these. The separator
                    and generous padding keep the panel from crowding the button row it hangs off. */}
                <Collapse in={areFiltersOpen}>
                    <Stack gap="lg" pt="xl" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
                        {/* disabled={!isDataReady} on every field here (see chat: "Filter dürfen erst
                            aufrufbar sein, wenn die Daten geladen sind") - groupOptions/documentLanguages/
                            etc. are all empty (or worse, a stale partial set) before their query resolves,
                            so letting the user interact early would just mean re-picking once real data
                            arrives. */}
                        {/* `searchable` on all three: these lists run to dozens of entries, and the
                            customer asked for the same type-to-filter behaviour the document filters
                            have (see chat: "Der Kunde findet es gerade bei Gruppe / Familie / System
                            gut auch manuell die Liste zu durchsuchen per Eingabe"). */}
                        {/* SimpleGrid, not `Group grow`, for every filter row in this panel: a Group
                            keeps all its fields on ONE line whatever the width, which on a phone
                            squeezed Gruppe/Familie/System into three ~80px columns whose placeholder
                            was already cut to "Auswählen..." (see the reported iPhone screenshot).
                            Below `sm` the fields stack full-width instead; from `sm` up the row looks
                            as before. */}
                        <SimpleGrid cols={{ base: 1, sm: 3 }}>
                            <MultiSelect
                                data={groupOptions}
                                disabled={!isDataReady}
                                label={L.searchMaskGroup}
                                nothingFoundMessage={L.noFilterMatch}
                                placeholder={groupIds.length > 0 ? undefined : L.searchMaskChoose}
                                rightSection={selectRightSection(groupIds.length > 0, () => handleGroupsChange([]))}
                                rightSectionPointerEvents={selectRightSectionPointerEvents(groupIds.length > 0)}
                                value={groupIds}
                                searchable
                                onChange={handleGroupsChange}
                            />
                            <MultiSelect
                                data={familyOptions}
                                disabled={!isDataReady}
                                label={L.searchMaskFamily}
                                nothingFoundMessage={L.noFilterMatch}
                                placeholder={familyIds.length > 0 ? undefined : L.searchMaskChoose}
                                rightSection={selectRightSection(familyIds.length > 0, () => handleFamiliesChange([]))}
                                rightSectionPointerEvents={selectRightSectionPointerEvents(familyIds.length > 0)}
                                value={familyIds}
                                searchable
                                onChange={handleFamiliesChange}
                            />
                            <MultiSelect
                                data={systemOptions}
                                disabled={!isDataReady}
                                label={L.searchMaskSystem}
                                nothingFoundMessage={L.noFilterMatch}
                                placeholder={systemIds.length > 0 ? undefined : L.searchMaskChoose}
                                rightSection={selectRightSection(systemIds.length > 0, () => handleSystemsChange([]))}
                                rightSectionPointerEvents={selectRightSectionPointerEvents(systemIds.length > 0)}
                                value={systemIds}
                                searchable
                                onChange={handleSystemsChange}
                            />
                        </SimpleGrid>
                        {/* Assortment entry (products only, see file comment): the checkbox row,
                            then Landesgesellschaft / Lifecycle / Planbar ab. The two selects the
                            checkbox stands in for are disabled while it is ticked; "Planbar ab" is
                            not, it combines with either. Labels come from the facet definitions in
                            catalog-config.json, so they read the same here and in the results
                            page's filter drawer. */}
                        {mode === 'products' && (
                            <>
                                <Checkbox
                                    checked={isGlobalAssortment}
                                    description={presetCaption || undefined}
                                    disabled={!isDataReady}
                                    label={L.searchMaskGlobalAssortment}
                                    onChange={(event) => handleGlobalAssortmentChange(event.currentTarget.checked)}
                                />
                                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                                    {subsidiaryFacet && (
                                        <MultiSelect
                                            data={subsidiaryOptions}
                                            disabled={!isDataReady || isGlobalAssortment}
                                            label={loc(subsidiaryFacet.label, locale, subsidiaryFacet.key)}
                                            nothingFoundMessage={L.noFilterMatch}
                                            placeholder={subsidiaries.length > 0 ? undefined : L.searchMaskChoose}
                                            rightSection={selectRightSection(subsidiaries.length > 0, () =>
                                                setSubsidiaries([])
                                            )}
                                            rightSectionPointerEvents={selectRightSectionPointerEvents(
                                                subsidiaries.length > 0
                                            )}
                                            value={subsidiaries}
                                            searchable
                                            onChange={setSubsidiaries}
                                        />
                                    )}
                                    {lifecycleFacet && (
                                        <MultiSelect
                                            data={lifecycleOptions}
                                            disabled={!isDataReady || isGlobalAssortment}
                                            label={loc(lifecycleFacet.label, locale, lifecycleFacet.key)}
                                            nothingFoundMessage={L.noFilterMatch}
                                            placeholder={lifecycles.length > 0 ? undefined : L.searchMaskChoose}
                                            rightSection={selectRightSection(lifecycles.length > 0, () =>
                                                setLifecycles([])
                                            )}
                                            rightSectionPointerEvents={selectRightSectionPointerEvents(
                                                lifecycles.length > 0
                                            )}
                                            value={lifecycles}
                                            searchable
                                            onChange={setLifecycles}
                                        />
                                    )}
                                    {plannableFacet && (
                                        <DatePickerInput
                                            disabled={!isDataReady}
                                            label={loc(plannableFacet.label, locale, plannableFacet.key)}
                                            placeholder={L.searchMaskChoose}
                                            value={isoToDate(plannableUntil)}
                                            valueFormat="DD.MM.YYYY"
                                            clearable
                                            onChange={(date) => setPlannableUntil(dateToIso(date))}
                                        />
                                    )}
                                </SimpleGrid>
                            </>
                        )}
                        {/* Own row, not squeezed in next to Gruppe/Familie/System (see chat: 5
                            columns of varying natural width looked cramped) - same filters as the
                            results page's Dokumentenfilter (DocumentFacetsPanel/DocumentFilterBar),
                            but as plain Selects here rather than a radio/checkbox list, since this
                            accordion panel is too narrow for a multi-row checkbox group. Hidden
                            entirely when there's nothing to filter by (<=1 option), same convention
                            as those two - so this row itself only renders once at least one of them has
                            something to show. */}
                        {mode === 'documents' && (documentLanguages.length > 1 || availableDocTypes.length > 1) && (
                            <SimpleGrid cols={{ base: 1, sm: 2 }}>
                                {documentLanguages.length > 1 && (
                                    <Select
                                        data={documentLanguages.map((code) => ({ value: code, label: languageLabel(code, locale) }))}
                                        disabled={!isDataReady}
                                        label={L.documentLanguage}
                                        nothingFoundMessage={L.noFilterMatch}
                                        placeholder={L.allLanguages}
                                        rightSection={selectRightSection(Boolean(documentLanguage), () =>
                                            setDocumentLanguage(null)
                                        )}
                                        rightSectionPointerEvents={selectRightSectionPointerEvents(
                                            Boolean(documentLanguage)
                                        )}
                                        value={documentLanguage}
                                        searchable
                                        onChange={setDocumentLanguage}
                                    />
                                )}
                                {availableDocTypes.length > 1 && (
                                    <MultiSelect
                                        data={availableDocTypes.map((type) => ({ value: type, label: humanizeDocType(type) }))}
                                        disabled={!isDataReady}
                                        label={L.documentCategory}
                                        nothingFoundMessage={L.noFilterMatch}
                                        placeholder={documentDocTypes.length > 0 ? undefined : L.searchMaskChoose}
                                        rightSection={selectRightSection(documentDocTypes.length > 0, () =>
                                            setDocumentDocTypes([])
                                        )}
                                        rightSectionPointerEvents={selectRightSectionPointerEvents(
                                            documentDocTypes.length > 0
                                        )}
                                        value={documentDocTypes}
                                        searchable
                                        onChange={setDocumentDocTypes}
                                    />
                                )}
                            </SimpleGrid>
                        )}
                    </Stack>
                </Collapse>
            </Stack>
        </Card>
    );
};
